import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { changePassword, getSuiteCRMStatus, connectSuiteCRM, disconnectSuiteCRM, testSuiteCRMConnection, SuiteCRMStatus, apiClient, exportMyData, deleteMyAccount } from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { GlassBadge } from '../ui/GlassBadge';
import { ModuleHeader } from '../ui/ModuleHeader';
import { Lock, Trash2, Copy, CreditCard, CheckCircle, Download, ShieldAlert } from 'lucide-react';
import AgentTokensPanel from '../AgentTokens/AgentTokensPanel';
import './UserProfile.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const PLAN_LABELS: Record<string, string> = { trial: 'Trial', growth: 'Growth', pro: 'Pro', enterprise: 'Enterprise' };

interface TimezonePickerProps {
    options: Array<{ value: string; label: string }>;
    value: string;
    onChange: (value: string) => void;
}

const TimezonePicker: React.FC<TimezonePickerProps> = ({ options, value, onChange }) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [highlightIdx, setHighlightIdx] = useState(0);
    const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);

    const updateRect = React.useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }, []);

    useEffect(() => {
        if (!open) return;
        updateRect();
        window.addEventListener('scroll', updateRect, true);
        window.addEventListener('resize', updateRect);
        return () => {
            window.removeEventListener('scroll', updateRect, true);
            window.removeEventListener('resize', updateRect);
        };
    }, [open, updateRect]);

    const selectedLabel = options.find(o => o.value === value)?.label || value;

    const filtered = React.useMemo(() => {
        if (!query) return options;
        const q = query.toLowerCase().replace(/\s+/g, '');
        return options.filter(o =>
            o.value.toLowerCase().replace(/\s+/g, '').includes(q) ||
            o.label.toLowerCase().replace(/\s+/g, '').includes(q)
        );
    }, [query, options]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            const insideWrapper = wrapperRef.current?.contains(target);
            const insideList = listRef.current?.contains(target);
            if (!insideWrapper && !insideList) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Prevent scroll chaining to the page when the dropdown hits top/bottom
    useEffect(() => {
        const el = listRef.current;
        if (!el || !open) return;
        const onWheel = (e: WheelEvent) => {
            const { scrollTop, scrollHeight, clientHeight } = el;
            const atTop = scrollTop === 0;
            const atBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;
            if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
                e.preventDefault();
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [open]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIdx(i => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIdx(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && filtered[highlightIdx]) {
            e.preventDefault();
            onChange(filtered[highlightIdx].value);
            setOpen(false);
            setQuery('');
        } else if (e.key === 'Escape') {
            setOpen(false);
            setQuery('');
        }
    };

    const dropdown = open && rect ? createPortal(
        <div
            ref={listRef}
            style={{
                position: 'fixed',
                top: rect.top,
                left: rect.left,
                width: rect.width,
                maxHeight: '260px',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                background: 'var(--panel)',
                border: '1px solid var(--stroke)',
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                zIndex: 10000,
                backdropFilter: 'blur(20px)',
            }}
        >
            {filtered.length === 0 ? (
                <div style={{ padding: '12px', color: 'var(--text-secondary)', textAlign: 'center', fontSize: '0.85rem' }}>
                    No matches
                </div>
            ) : filtered.map((o, i) => (
                <div
                    key={o.value}
                    onMouseEnter={() => setHighlightIdx(i)}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
                    style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        color: 'var(--text)',
                        background: i === highlightIdx ? 'var(--panel-strong)' : 'transparent',
                        fontWeight: o.value === value ? 600 : 400,
                    }}
                >
                    {o.label}
                </div>
            ))}
        </div>,
        document.body
    ) : null;

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <input
                ref={inputRef}
                type="text"
                value={open ? query : selectedLabel}
                onFocus={() => { setOpen(true); setQuery(''); setHighlightIdx(0); }}
                onChange={e => { setQuery(e.target.value); setHighlightIdx(0); setOpen(true); }}
                onKeyDown={handleKeyDown}
                placeholder="Search timezone…"
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'var(--panel-strong)',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text)',
                    fontSize: '0.9rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                }}
            />
            {dropdown}
        </div>
    );
};


const UserProfile: React.FC = () => {
    const { user, updateProfile, logout } = useAuth();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);

    // GDPR Data & Privacy state
    const [exporting, setExporting] = useState(false);
    const [privacyError, setPrivacyError] = useState('');
    const [privacyNotice, setPrivacyNotice] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleExportData = async () => {
        setPrivacyError('');
        setPrivacyNotice('');
        setExporting(true);
        try {
            const blob = await exportMyData();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            a.href = url;
            a.download = `aime_data_export_${today}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setPrivacyNotice('Data export downloaded.');
        } catch (e: any) {
            setPrivacyError(getApiError(e, 'Failed to export data. Please try again.'));
        } finally {
            setExporting(false);
        }
    };

    const handleConfirmDelete = async () => {
        setPrivacyError('');
        setDeleting(true);
        try {
            await deleteMyAccount(deleteConfirmEmail);
            // account is gone — log out and bounce to login
            logout();
            navigate('/login', { replace: true });
        } catch (e: any) {
            setPrivacyError(getApiError(e, 'Failed to delete account.'));
            setDeleting(false);
        }
    };

    // Billing summary state
    const [billingInfo, setBillingInfo] = useState<{ plan: string | null; status: string; stripe_customer_id: string | null } | null>(null);
    const [copiedCustomerId, setCopiedCustomerId] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [formData, setFormData] = useState({
        full_name: user?.full_name || '',
        bio: user?.bio || '',
        timezone: user?.timezone || 'UTC',
        email_notifications: user?.email_notifications ?? true,
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // SuiteCRM State
    const [suiteCRMStatus, setSuiteCRMStatus] = useState<SuiteCRMStatus | null>(null);
    const [suiteCRMLoading, setSuiteCRMLoading] = useState(false);
    const [suiteCRMConnecting, setSuiteCRMConnecting] = useState(false);
    const [suiteCRMError, setSuiteCRMError] = useState('');
    const [suiteCRMSuccess, setSuiteCRMSuccess] = useState('');
    const [suiteCRMCredentials, setSuiteCRMCredentials] = useState({
        url: '',
        username: '',
        password: ''
    });

    useEffect(() => {
        loadSuiteCRMStatus();
        apiClient.get('/billing/status').then(r => setBillingInfo(r.data)).catch(() => {});
    }, []);

    const loadSuiteCRMStatus = async () => {
        setSuiteCRMLoading(true);
        try {
            const status = await getSuiteCRMStatus();
            setSuiteCRMStatus(status);
            if (status.url) {
                setSuiteCRMCredentials(prev => ({ ...prev, url: status.url! }));
            }
        } catch (error) {
            console.error('Failed to load SuiteCRM status', error);
        } finally {
            setSuiteCRMLoading(false);
        }
    };

    const handleConnectSuiteCRM = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuiteCRMError('');
        setSuiteCRMSuccess('');
        setSuiteCRMConnecting(true);

        try {
            const status = await connectSuiteCRM(suiteCRMCredentials);
            setSuiteCRMStatus(status);
            setSuiteCRMSuccess('Successfully connected to SuiteCRM');
            // Clear password
            setSuiteCRMCredentials(prev => ({ ...prev, password: '' }));
        } catch (err: any) {
            setSuiteCRMError(getApiError(err, 'Failed to connect to SuiteCRM'));
        } finally {
            setSuiteCRMConnecting(false);
        }
    };

    const handleDisconnectSuiteCRM = async () => {
        if (!window.confirm('Are you sure you want to disconnect SuiteCRM?')) return;
        
        setSuiteCRMConnecting(true);
        try {
            await disconnectSuiteCRM();
            setSuiteCRMStatus({ connected: false });
            setSuiteCRMSuccess('Successfully disconnected from SuiteCRM');
            setSuiteCRMCredentials(prev => ({ ...prev, username: '', password: '' }));
        } catch (err: any) {
            setSuiteCRMError(getApiError(err, 'Failed to disconnect'));
        } finally {
            setSuiteCRMConnecting(false);
        }
    };

    const handleTestSuiteCRM = async () => {
        setSuiteCRMConnecting(true);
        setSuiteCRMSuccess('');
        setSuiteCRMError('');
        try {
            const status = await testSuiteCRMConnection();
            if (status.connected) {
                setSuiteCRMSuccess('Connection test passed: SuiteCRM is accessible');
            } else {
                setSuiteCRMError('Connection test failed: ' + (status.message || 'Unknown error'));
            }
        } catch (err: any) {
            setSuiteCRMError(getApiError(err, 'Connection test failed'));
        } finally {
            setSuiteCRMConnecting(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        setError('');
        setSuccess('');
        setIsSaving(true);

        try {
            await updateProfile(formData);
            setSuccess('Profile updated successfully');
            setIsEditing(false);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to update profile'));
        } finally {
            setIsSaving(false);
        }
    };

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters');
            return;
        }

        setIsChangingPassword(true);

        try {
            await changePassword(passwordData.currentPassword, passwordData.newPassword);
            setPasswordSuccess('Password changed successfully');
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err: any) {
            setPasswordError(getApiError(err, 'Failed to change password'));
        } finally {
            setIsChangingPassword(false);
        }
    };

    if (!user) {
        return (
            <div className="user-profile-page">
                <GlassCard padding="lg">
                    <p>Loading...</p>
                </GlassCard>
            </div>
        );
    }

    // Full IANA timezone list from the browser's built-in database (~400 zones).
    // Falls back to a curated list for older browsers that don't support
    // Intl.supportedValuesOf (Safari < 15.4, etc.).
    const timezoneOptions = (() => {
        const formatLabel = (tz: string): string => {
            try {
                const offset = new Intl.DateTimeFormat('en-GB', {
                    timeZone: tz,
                    timeZoneName: 'shortOffset',
                }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
                return `${tz.replace(/_/g, ' ')} (${offset})`;
            } catch {
                return tz;
            }
        };

        let zones: string[];
        const intlAny = Intl as any;
        if (typeof intlAny.supportedValuesOf === 'function') {
            zones = intlAny.supportedValuesOf('timeZone');
        } else {
            zones = [
                'UTC',
                'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi',
                'America/Anchorage', 'America/Argentina/Buenos_Aires', 'America/Bogota',
                'America/Chicago', 'America/Denver', 'America/Halifax', 'America/Los_Angeles',
                'America/Mexico_City', 'America/New_York', 'America/Phoenix', 'America/Sao_Paulo',
                'America/Toronto', 'America/Vancouver',
                'Asia/Bangkok', 'Asia/Dubai', 'Asia/Hong_Kong', 'Asia/Jakarta', 'Asia/Karachi',
                'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Riyadh',
                'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Taipei', 'Asia/Tel_Aviv',
                'Asia/Tokyo',
                'Atlantic/Azores', 'Atlantic/Reykjavik',
                'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Darwin', 'Australia/Hobart',
                'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney',
                'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/Brussels',
                'Europe/Bucharest', 'Europe/Dublin', 'Europe/Helsinki', 'Europe/Istanbul',
                'Europe/Lisbon', 'Europe/London', 'Europe/Madrid', 'Europe/Moscow',
                'Europe/Oslo', 'Europe/Paris', 'Europe/Prague', 'Europe/Rome', 'Europe/Stockholm',
                'Europe/Vienna', 'Europe/Warsaw', 'Europe/Zurich',
                'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Honolulu',
            ];
        }

        // Sort alphabetically but keep UTC at the top
        const sorted = zones.filter(z => z !== 'UTC').sort();
        return [
            { value: 'UTC', label: formatLabel('UTC') },
            ...sorted.map(z => ({ value: z, label: formatLabel(z) })),
        ];
    })();

    return (
        <div className="user-profile-page">
            <ModuleHeader
                title="My Profile"
                breadcrumb={['Home', 'Profile']}
            />

            <div className="profile-content">
                {/* Profile Info Section */}
                <GlassCard variant="default" padding="lg" className="profile-section-card">
                    <div className="section-header">
                        <h2>Profile Information</h2>
                        {!isEditing && (
                            <GlassButton
                                variant="primary"
                                size="sm"
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </GlassButton>
                        )}
                    </div>

                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}

                    <div className="profile-card">
                        <div className="avatar-section">
                            <div className="avatar-large">
                                {user.avatar_url ? (
                                    <img src={user.avatar_url} alt={user.username} />
                                ) : (
                                    <span className="avatar-placeholder-large">
                                        {(user.full_name || user.username).charAt(0).toUpperCase()}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="profile-fields">
                            <div className="field-row">
                                <label>Username</label>
                                <div className="field-value">{user.username}</div>
                            </div>

                            <div className="field-row">
                                <label>Email</label>
                                <div className="field-value">{user.email}</div>
                            </div>

                            <div className="field-row">
                                <label>Full Name</label>
                                {isEditing ? (
                                    <GlassInput
                                        value={formData.full_name}
                                        onChange={(value) => setFormData(prev => ({ ...prev, full_name: value }))}
                                        placeholder="Enter your full name"
                                    />
                                ) : (
                                    <div className="field-value">{user.full_name || '-'}</div>
                                )}
                            </div>

                            <div className="field-row">
                                <label>Role</label>
                                <div className="field-value">
                                    <GlassBadge variant={user.role === 'admin' ? 'danger' : 'info'}>
                                        {user.role}
                                    </GlassBadge>
                                </div>
                            </div>

                            <div className="field-row">
                                <label>Bio</label>
                                {isEditing ? (
                                    <textarea
                                        name="bio"
                                        value={formData.bio}
                                        onChange={handleChange}
                                        placeholder="Tell us about yourself"
                                        rows={3}
                                        className="glass-textarea"
                                    />
                                ) : (
                                    <div className="field-value">{user.bio || '-'}</div>
                                )}
                            </div>

                            <div className="field-row">
                                <label>Timezone</label>
                                {isEditing ? (
                                    <TimezonePicker
                                        options={timezoneOptions}
                                        value={formData.timezone}
                                        onChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}
                                    />
                                ) : (
                                    <div className="field-value">{user.timezone || 'UTC'}</div>
                                )}
                            </div>

                            <div className="field-row">
                                <label>Email Notifications</label>
                                {isEditing ? (
                                    <label className="toggle-label">
                                        <input
                                            type="checkbox"
                                            name="email_notifications"
                                            checked={formData.email_notifications}
                                            onChange={handleChange}
                                        />
                                        <span>Receive email notifications</span>
                                    </label>
                                ) : (
                                    <div className="field-value">
                                        {user.email_notifications ? 'Enabled' : 'Disabled'}
                                    </div>
                                )}
                            </div>

                            {isEditing && (
                                <div className="button-row">
                                    <GlassButton
                                        variant="ghost"
                                        onClick={() => {
                                            setIsEditing(false);
                                            setFormData({
                                                full_name: user.full_name || '',
                                                bio: user.bio || '',
                                                timezone: user.timezone || 'UTC',
                                                email_notifications: user.email_notifications ?? true,
                                            });
                                        }}
                                    >
                                        Cancel
                                    </GlassButton>
                                    <GlassButton
                                        variant="primary"
                                        onClick={handleSave}
                                        loading={isSaving}
                                        disabled={isSaving}
                                    >
                                        Save Changes
                                    </GlassButton>
                                </div>
                            )}
                        </div>
                    </div>
                </GlassCard>

                {/* Change Password Section */}
                <GlassCard variant="default" padding="lg" className="profile-section-card">
                    <div className="section-header">
                        <h2>Change Password</h2>
                    </div>

                    {passwordError && <div className="error-message">{passwordError}</div>}
                    {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}

                    <form onSubmit={handlePasswordChange} className="password-form">
                        <div className="field-row">
                            <label>Current Password</label>
                            <GlassInput
                                type="password"
                                value={passwordData.currentPassword}
                                onChange={(value) => setPasswordData(prev => ({ ...prev, currentPassword: value }))}
                                placeholder="Enter current password"
                            />
                        </div>

                        <div className="field-row">
                            <label>New Password</label>
                            <GlassInput
                                type="password"
                                value={passwordData.newPassword}
                                onChange={(value) => setPasswordData(prev => ({ ...prev, newPassword: value }))}
                                placeholder="Enter new password"
                            />
                        </div>

                        <div className="field-row">
                            <label>Confirm New Password</label>
                            <GlassInput
                                type="password"
                                value={passwordData.confirmPassword}
                                onChange={(value) => setPasswordData(prev => ({ ...prev, confirmPassword: value }))}
                                placeholder="Confirm new password"
                            />
                        </div>

                        <div className="button-row">
                            <GlassButton
                                type="submit"
                                variant="primary"
                                loading={isChangingPassword}
                                disabled={isChangingPassword}
                            >
                                Change Password
                            </GlassButton>
                        </div>
                    </form>
                </GlassCard>

                {/* SuiteCRM Integration Section */}
                {user?.company_id ? (
                    /* Enterprise users — read-only company CRM info */
                    <GlassCard variant="default" padding="lg" className="profile-section-card">
                        <div className="section-header">
                            <h2>Company CRM</h2>
                            <GlassBadge variant="info">Enterprise</GlassBadge>
                        </div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0 0 12px' }}>
                            Your CRM access is managed by your company administrator.
                            Contact them to configure or update the CRM connection.
                        </p>
                        {suiteCRMStatus?.connected && (
                            <div className="suitecrm-connected-view">
                                <div className="info-row">
                                    <label>CRM Status:</label>
                                    <GlassBadge variant="success">Connected</GlassBadge>
                                </div>
                                {suiteCRMStatus.url && (
                                    <div className="info-row">
                                        <label>CRM URL:</label>
                                        <span>{suiteCRMStatus.url}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </GlassCard>
                ) : (
                    /* Individual users — full personal CRM connection management */
                    <GlassCard variant="default" padding="lg" className="profile-section-card">
                        <div className="section-header">
                            <h2>SuiteCRM Integration</h2>
                            {suiteCRMStatus?.connected && (
                                <GlassBadge variant="success">Connected</GlassBadge>
                            )}
                        </div>

                        {suiteCRMError && <div className="error-message">{suiteCRMError}</div>}
                        {suiteCRMSuccess && <div className="success-message">{suiteCRMSuccess}</div>}

                        {suiteCRMStatus?.connected ? (
                            <div className="suitecrm-connected-view">
                                <div className="info-row">
                                    <label>SuiteCRM URL:</label>
                                    <span>{suiteCRMStatus.url}</span>
                                </div>
                                <div className="info-row">
                                    <label>Connected User:</label>
                                    <span>{suiteCRMStatus.username}</span>
                                </div>
                                <div className="info-row">
                                    <label>Connected Since:</label>
                                    <span>{new Date(suiteCRMStatus.connected_at || '').toLocaleDateString()}</span>
                                </div>

                                <div className="button-row">
                                    <GlassButton
                                        variant="ghost"
                                        onClick={handleTestSuiteCRM}
                                        loading={suiteCRMConnecting}
                                        disabled={suiteCRMConnecting}
                                    >
                                        Test Connection
                                    </GlassButton>
                                    <GlassButton
                                        variant="danger"
                                        onClick={handleDisconnectSuiteCRM}
                                        loading={suiteCRMConnecting}
                                        disabled={suiteCRMConnecting}
                                    >
                                        Disconnect
                                    </GlassButton>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleConnectSuiteCRM} className="suitecrm-form">
                                <div className="field-row">
                                    <label>SuiteCRM URL</label>
                                    <GlassInput
                                        type="url"
                                        value={suiteCRMCredentials.url}
                                        onChange={(value) => setSuiteCRMCredentials(prev => ({ ...prev, url: value }))}
                                        placeholder="https://your-suitecrm-instance.com"
                                    />
                                </div>

                                <div className="field-row">
                                    <label>Username</label>
                                    <GlassInput
                                        value={suiteCRMCredentials.username}
                                        onChange={(value) => setSuiteCRMCredentials(prev => ({ ...prev, username: value }))}
                                        placeholder="SuiteCRM Username"
                                    />
                                </div>

                                <div className="field-row">
                                    <label>Password</label>
                                    <GlassInput
                                        type="password"
                                        value={suiteCRMCredentials.password}
                                        onChange={(value) => setSuiteCRMCredentials(prev => ({ ...prev, password: value }))}
                                        placeholder="SuiteCRM Password"
                                    />
                                </div>

                                <div className="button-row">
                                    <GlassButton
                                        type="submit"
                                        variant="primary"
                                        loading={suiteCRMConnecting}
                                        disabled={suiteCRMConnecting}
                                    >
                                        Connect to SuiteCRM
                                    </GlassButton>
                                </div>
                            </form>
                        )}
                    </GlassCard>
                )}


                {/* Billing Summary Section */}
                <GlassCard variant="default" padding="lg" className="profile-section-card">
                    <div className="section-header">
                        <h2><CreditCard size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />Billing</h2>
                        {billingInfo?.plan && (
                            <GlassBadge variant="info">{PLAN_LABELS[billingInfo.plan] ?? billingInfo.plan}</GlassBadge>
                        )}
                    </div>

                    {billingInfo?.stripe_customer_id && (
                        <div className="field-row" style={{ marginBottom: '1rem' }}>
                            <label>Customer Number</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <code style={{
                                    background: 'var(--panel-strong)',
                                    border: '1px solid var(--stroke)',
                                    borderRadius: 6,
                                    padding: '4px 10px',
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    color: 'var(--text)',
                                }}>
                                    {billingInfo.stripe_customer_id}
                                </code>
                                <button
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}
                                    onClick={() => {
                                        navigator.clipboard.writeText(billingInfo!.stripe_customer_id!);
                                        setCopiedCustomerId(true);
                                        setTimeout(() => setCopiedCustomerId(false), 2000);
                                    }}
                                    title="Copy customer number"
                                >
                                    {copiedCustomerId ? <CheckCircle size={15} color="#10b981" /> : <Copy size={15} />}
                                </button>
                            </div>
                        </div>
                    )}

                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0 0 1rem' }}>
                        View your full billing history, invoices, and manage your subscription plan.
                    </p>

                    <GlassButton variant="secondary" size="sm" onClick={() => navigate('/billing')}>
                        View Billing &amp; Invoices
                    </GlassButton>
                </GlassCard>

                {/* API Tokens (agent access) — panel self-hides when the feature is dark (404) */}
                <AgentTokensPanel />

                {/* Data & Privacy (GDPR self-service) */}
                <GlassCard variant="default" padding="lg" className="profile-section-card">
                    <div className="section-header">
                        <h2><ShieldAlert size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />Data &amp; Privacy</h2>
                    </div>

                    {privacyError && (
                        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '8px 12px', color: '#ef4444', marginBottom: 12, fontSize: '0.875rem' }}>
                            {privacyError}
                        </div>
                    )}
                    {privacyNotice && (
                        <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, padding: '8px 12px', color: '#10b981', marginBottom: 12, fontSize: '0.875rem' }}>
                            {privacyNotice}
                        </div>
                    )}

                    <div style={{ marginBottom: 20 }}>
                        <h3 style={{ fontSize: '1rem', margin: '0 0 6px', color: 'var(--text)' }}>Export My Data</h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0 0 10px' }}>
                            Download a ZIP archive containing all personal data we hold for your account, in machine-readable JSON format (GDPR Article 15).
                        </p>
                        <GlassButton variant="secondary" size="sm" onClick={handleExportData} disabled={exporting}>
                            <Download size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                            {exporting ? 'Preparing export…' : 'Export My Data'}
                        </GlassButton>
                    </div>

                    <div style={{ borderTop: '1px solid var(--stroke)', paddingTop: 20 }}>
                        <h3 style={{ fontSize: '1rem', margin: '0 0 6px', color: 'var(--text)' }}>Delete My Account</h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0 0 10px' }}>
                            Permanently delete your account and all associated personal data within 24 hours (GDPR Article 17). This cannot be undone. If you have an active paid subscription, cancel it first via Billing.
                        </p>
                        <GlassButton variant="danger" size="sm" onClick={() => { setPrivacyError(''); setDeleteConfirmEmail(''); setShowDeleteModal(true); }}>
                            <Trash2 size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                            Delete My Account
                        </GlassButton>
                    </div>
                </GlassCard>

            </div>

            {showDeleteModal && createPortal(
                <div
                    onClick={() => !deleting && setShowDeleteModal(false)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
                >
                    <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: '100%' }}>
                        <GlassCard variant="strong" padding="lg">
                            <h2 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ShieldAlert size={22} color="#ef4444" /> Delete Your Account
                            </h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 16 }}>
                                This will permanently delete your account, content, projects, integrations, and all personal data. This cannot be undone.
                            </p>
                            <p style={{ color: 'var(--text)', fontSize: '0.875rem', marginBottom: 8 }}>
                                Type your email address (<strong>{user?.email}</strong>) to confirm:
                            </p>
                            <GlassInput
                                value={deleteConfirmEmail}
                                onChange={(value) => setDeleteConfirmEmail(value)}
                                placeholder={user?.email || 'your@email.com'}
                                disabled={deleting}
                                autoFocus
                            />
                            {privacyError && (
                                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '8px 12px', color: '#ef4444', marginTop: 12, fontSize: '0.875rem' }}>
                                    {privacyError}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                                <GlassButton variant="secondary" size="sm" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                                    Cancel
                                </GlassButton>
                                <GlassButton
                                    variant="danger"
                                    size="sm"
                                    onClick={handleConfirmDelete}
                                    disabled={deleting || deleteConfirmEmail.trim().toLowerCase() !== (user?.email || '').trim().toLowerCase()}
                                >
                                    {deleting ? 'Deleting…' : 'Permanently Delete'}
                                </GlassButton>
                            </div>
                        </GlassCard>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default UserProfile;
